import { useMemo } from "react";

import { useCustomReports } from "@/hooks/useCustomReports";

import type { CustomReportSummary } from "@/types/reports";

/** At most this many reports in a search dropdown: it is a jump list, not the catalog. */
const MAX_HITS = 5;

/**
 * The caller's own custom reports whose name or description matches a search
 * term — matched in the browser over the list the Reports module already
 * caches, the way `useMarketSearch` matches the topology. Every word in the
 * term must appear; name matches rank first.
 *
 * Empty without the `report_builder` feature (the list query never runs) or
 * until the term is long enough (`active`).
 */
export function useReportSearch(query: string, active: boolean): CustomReportSummary[] {
  const { data } = useCustomReports({ enabled: active });
  return useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!active || words.length === 0 || !data) return [];
    const scored = data
      .map((r) => {
        const name = r.name.toLowerCase();
        const text = `${name} ${(r.description ?? "").toLowerCase()}`;
        if (!words.every((w) => text.includes(w))) return null;
        return { r, score: words.every((w) => name.includes(w)) ? 0 : 1 };
      })
      .filter((x): x is { r: CustomReportSummary; score: number } => x !== null);
    return scored
      .sort((a, b) => a.score - b.score || a.r.name.localeCompare(b.r.name))
      .slice(0, MAX_HITS)
      .map((x) => x.r);
  }, [query, active, data]);
}
